import axios from '../utils/axiosConfig';
import { Event, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class EventsService {
  private baseURL = `${API_BASE_URL}/events`;

  // Get all Events
  async getAllEvents(includeHidden: boolean = false, upcomingOnly: boolean = false): Promise<Event[]> {
    try {
      let url = this.baseURL;
      const params = new URLSearchParams();
      
      if (includeHidden) params.append('includeHidden', 'true');
      if (upcomingOnly) params.append('upcoming', 'true');
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get<ApiListResponse<Event>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch events');
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  // Get visible Events (for homepage)
  async getVisibleEvents(): Promise<Event[]> {
    return this.getAllEvents(false, false);
  }

  // Get upcoming Events
  async getUpcomingEvents(): Promise<Event[]> {
    return this.getAllEvents(false, true);
  }

  // Get Event by ID
  async getEventById(id: number): Promise<Event> {
    try {
      const response = await axios.get<ApiResponse<Event>>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch event');
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }

  // Create new Event
  async createEvent(formData: FormData): Promise<Event> {
    try {
      const response = await axios.post<ApiResponse<Event>>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create event');
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // Update Event
  async updateEvent(id: number, formData: FormData): Promise<Event> {
    try {
      const response = await axios.put<ApiResponse<Event>>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update event');
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // Delete Event
  async deleteEvent(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Toggle visibility
  async toggleVisibility(id: number): Promise<Event> {
    try {
      const response = await axios.patch<ApiResponse<Event>>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // Helper method to get image URL
  getImageUrl(imagePath: string | undefined | null): string {
    if (!imagePath || typeof imagePath !== 'string') {
      return '/assets/images/default-event.jpg';
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // If it's a relative path starting with /uploads or /assets, return as is (proxy will handle it)
    if (imagePath.startsWith('/uploads') || imagePath.startsWith('/assets')) {
      return imagePath;
    }
    
    // Otherwise assume it's just a filename, prepend /uploads/
    return `/uploads/${imagePath}`;
  }

  // Format date for display
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  }

  // Format time for display
  formatTime(timeString: string): string {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  }

  // Get event duration
  getEventDuration(startTime: string, endTime: string): string {
    return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
  }

  // Check if event is free
  isFreeEvent(price?: number | null): boolean {
    return price === null || price === undefined || price === 0;
  }

  // Format price
  formatPrice(price?: number | null): string {
    if (this.isFreeEvent(price)) {
      return 'Free for employees';
    }
    return `$${price?.toFixed(2)}`;
  }

  // Get month abbreviation
  getMonthAbbr(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  }

  // Get day number
  getDayNumber(dateString: string): string {
    const date = new Date(dateString);
    return date.getDate().toString();
  }

  // Check if event is upcoming
  isUpcoming(eventDate: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const event = new Date(eventDate);
    return event >= today;
  }

  // Check if event is today
  isToday(eventDate: string): boolean {
    const today = new Date();
    const event = new Date(eventDate);
    return today.toDateString() === event.toDateString();
  }
}

export default new EventsService();
